/**
 * Farbpalette fuer Lebensbereiche - und seit D-02 fuer jede Farbauswahl
 * (Kategorien, Markierungen). Frei gewaehlte Farben: `domain/colors/oklch.ts`.
 *
 * DIE REIHENFOLGE IST BEDEUTSAM. Die ersten VIER Farben sind mit dem
 * Farbpruefer aus der Datenvisualisierungs-Richtlinie geprueft und bestehen
 * in hell UND dunkel alle harten Schwellen, auch wenn alle vier gleichzeitig
 * sichtbar sind (`--pairs all`):
 *
 *   hell:   CVD ΔE 10.9 · Normalsicht ΔE 16.8 · Kontrast alle >= 3:1
 *   dunkel: CVD ΔE 10.9 · Normalsicht ΔE 16.5 · Kontrast alle >= 3:1
 *
 * Die beiden letzten Farben bestehen diese Pruefung NICHT. Das ist keine
 * Nachlaessigkeit, sondern eine Eigenschaft des Farbsehens: bei Deuteranopie
 * fallen Gruen und Bernstein praktisch zusammen (ΔE 1.7), und mit fuenf oder
 * mehr gleichzeitig sichtbaren Farbtoenen gibt es nachweislich keine
 * Anordnung, die die Schwellen haelt - die Suche darueber ist gelaufen.
 *
 * Deshalb gilt im ganzen System: **die Bereichsfarbe steht nie allein.**
 * Ueberall, wo ein Bereichspunkt erscheint, steht sein Name daneben
 * (Filterleiste, Bereichsliste, Termin- und Aufgabenformular). Im Kalender
 * traegt der Titel des Termins die Identitaet, die Farbe ist Beiwerk.
 *
 * Wer mehr als vier Bereiche anlegt, bekommt also weiterhin unterscheidbare
 * Eintraege - nur eben ueber den Namen statt ueber die Farbe allein.
 */
import { eigeneFarbe } from '@/domain/colors/oklch';

export interface AreaColorOption {
  /** Interner Schluessel - nur fuer die Auswahl in der Oberflaeche. */
  key: string;
  /** Deutscher Anzeigename. */
  label: string;
  /** Der gespeicherte Wert (landet in `Area.color`), Hellmodus. */
  value: string;
  /** Ob dieser Ton Teil der geprueften Vierergruppe ist. */
  validated: boolean;
}

/**
 * Alle Werte in OKLCH mit einheitlicher Helligkeit (0.57) und Chroma (0.21).
 * Die Dunkelvariante wird in `styles/tailwind.css` daraus abgeleitet, damit
 * nur EIN Wert je Bereich gespeichert werden muss.
 */
export const AREA_COLORS: AreaColorOption[] = [
  { key: 'petrol', label: 'Petrol', value: 'oklch(0.57 0.21 213)', validated: true },
  { key: 'indigo', label: 'Indigo', value: 'oklch(0.57 0.21 283)', validated: true },
  { key: 'pink', label: 'Pink', value: 'oklch(0.57 0.21 359)', validated: true },
  { key: 'bernstein', label: 'Bernstein', value: 'oklch(0.57 0.21 74)', validated: true },
  { key: 'gruen', label: 'Grün', value: 'oklch(0.57 0.21 143)', validated: false },
  { key: 'magenta', label: 'Magenta', value: 'oklch(0.57 0.21 321)', validated: false },
  // D-02: erweiterte Auswahl. Gleiche Helligkeit, Buntheit dort gesenkt, wo
  // der Farbton sonst den Bildschirmfarbraum verlaesst. Orange fehlt bewusst:
  // es ist die Akzentfarbe des Designs (D-05).
  { key: 'rot', label: 'Rot', value: 'oklch(0.57 0.2 25)', validated: false },
  { key: 'gelb', label: 'Gelb', value: 'oklch(0.62 0.13 100)', validated: false },
  { key: 'limette', label: 'Limette', value: 'oklch(0.6 0.16 125)', validated: false },
  { key: 'tuerkis', label: 'Türkis', value: 'oklch(0.6 0.11 185)', validated: false },
  { key: 'himmel', label: 'Himmelblau', value: 'oklch(0.6 0.13 242)', validated: false },
  { key: 'blau', label: 'Blau', value: 'oklch(0.55 0.2 262)', validated: false },
  { key: 'violett', label: 'Violett', value: 'oklch(0.57 0.2 303)', validated: false },
  { key: 'rose', label: 'Rosé', value: 'oklch(0.62 0.12 10)', validated: false },
  { key: 'braun', label: 'Braun', value: 'oklch(0.55 0.09 55)', validated: false },
  { key: 'oliv', label: 'Oliv', value: 'oklch(0.57 0.1 110)', validated: false },
  { key: 'schiefer', label: 'Schiefer', value: 'oklch(0.57 0.05 250)', validated: false },
  { key: 'grau', label: 'Grau', value: 'oklch(0.6 0.01 85)', validated: false },
];

/**
 * Vorschlaege fuer Bereichsfarben (D-09, aus dem freigegebenen Entwurf), als
 * Hex fuer den dunklen Grund gedacht. `value` ist der gespeicherte Wert: dieselbe
 * Farbe, in den lesbaren Bereich gezogen (`eigeneFarbe`).
 */
const VORSCHLAEGE: { key: string; label: string; hex: string }[] = [
  { key: 'v-gruen', label: 'Salbei', hex: '#8FAE7E' },
  { key: 'v-blau', label: 'Taubenblau', hex: '#7E93AB' },
  { key: 'v-gold', label: 'Sand', hex: '#C4A57F' },
  { key: 'v-lila', label: 'Flieder', hex: '#A89BB8' },
  { key: 'v-rost', label: 'Rost', hex: '#C98A72' },
  { key: 'v-senf', label: 'Senf', hex: '#B5A06A' },
  { key: 'v-teal', label: 'Petrol', hex: '#6FA3A0' },
  { key: 'v-rosa', label: 'Altrosa', hex: '#B88AA0' },
  { key: 'v-grau', label: 'Graublau', hex: '#9AA5B8' },
];

export const BEREICHS_VORSCHLAEGE: AreaColorOption[] = VORSCHLAEGE.map((v) => ({
  key: v.key, label: v.label, value: eigeneFarbe(v.hex)!, validated: false,
}));

/** Wie viele Farben gleichzeitig nachweislich unterscheidbar sind. */
export const VALIDATED_COLOR_COUNT = AREA_COLORS.filter((c) => c.validated).length;

export const DEFAULT_AREA_COLOR = AREA_COLORS[0]!.value;

/**
 * Farbe fuer Eintraege ohne Bereich - bewusst entsaettigt, nie eine Kennfarbe.
 * Warmes Grau passend zum Grundton (D-05); frueher ein Blaugrau.
 */
export const NO_AREA_COLOR = eigeneFarbe('#8c8473')!; // D-09: neutral, wie `ink-quiet` im Entwurf
